import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Routes, Route } from "react-router-dom";
import LoginPage from "@client.pages/Login";
import DemoPage from "@client.pages/Demo";
import {
  DashboardDefaultPage,
  DashboardTeamPage,
  DashboardOwnPage,
} from "@client.pages/Dashboard";
import {
  ReportsDefaultPage,
  ReportsCompanyPage,
  ReportsParticipantsPage,
  ReportsComparisonsPage,
  ReportsTrendsPage,
  ReportsCostPage,
  ReportsMeetingsPage,
} from "@client.pages/Reports";
import UsersManagementPage from "@client.pages/Settings/UsersManagement";
import InsightsSettingsPage from "@client.pages/Settings/InsightsSettings";
import LogoutPage from "@client.pages/Logout";
import AuthorizedLayout from "@client.components/AuthorizedLayout";
import UnauthorizedLayout from "@client.components/UnauthorizedLayout";
import RequireAuth from "@client.components/RequireAuth";
import Pages from "@client.enums/pages";
import {
  getProfileAsync,
  makeProfileLoading,
} from "@client.pages/Login/reducer";
import i18n, { DEFAULT_LANGUAGE } from "./i18n";
import Loader from "@client.components/Loader";
import { AllRolesExceptViewer } from "@client.enums/userRoles";
import RequireRole from "@client.components/RequireRole";
import DashboardInsights from "@client.pages/Dashboard/Insights";

const AppRoutes = () => {
  const [isLoadingTranslations, setIsLoadingTranslations] = useState(true);
  const dispatch = useDispatch();
  const loadingProfile = useSelector(makeProfileLoading);

  useEffect(() => {
    const existingLanguges = [DEFAULT_LANGUAGE];
    let language = DEFAULT_LANGUAGE;

    if (i18n.language) {
      language =
        existingLanguges.find(
          (l) => l === i18n.language || i18n.language.startsWith(l)
        ) || DEFAULT_LANGUAGE;
    }
    import(`@client.i18n/${language}.json`)
      .then((translations) => {
        i18n.changeLanguage(language);

        i18n.addResourceBundle(
          language,
          "translation",
          translations.default,
          true,
          true
        );
        setIsLoadingTranslations(false);
      })
      .catch((error) => {
        console.error("Error loading translations:", error);
      });
  }, []);

  useEffect(() => {
    dispatch(getProfileAsync());
  }, [dispatch]);

  if (isLoadingTranslations || loadingProfile) {
    return (
      <div className="app page-loading">
        <Loader loading={true} />
      </div>
    );
  }

  return (
    <div className="app">
      <Routes>
        <Route element={<UnauthorizedLayout />}>
          <Route path={Pages.Login} element={<LoginPage />} />
          <Route path={Pages.Demo} element={<DemoPage />} />
        </Route>
        <Route element={<RequireAuth />}>
          <Route element={<AuthorizedLayout />}>
            <Route
              path={Pages.DashboardInsights}
              element={<DashboardInsights />}
            />
            <Route path={Pages.DashboardOwn} element={<DashboardOwnPage />} />
            <Route element={<RequireRole roles={AllRolesExceptViewer} />}>
              <Route
                path={Pages.DashboardTeam}
                element={<DashboardTeamPage />}
              />
              <Route
                path={Pages.Dashboard}
                element={<DashboardDefaultPage />}
              />
              <Route
                path={Pages.ReportsCompany}
                element={<ReportsCompanyPage />}
              />
              <Route
                path={Pages.ReportsMeetings}
                element={<ReportsMeetingsPage />}
              />
              <Route
                path={Pages.ReportsParticipants}
                element={<ReportsParticipantsPage />}
              />
              <Route
                path={Pages.ReportsComparisons}
                element={<ReportsComparisonsPage />}
              />
              <Route path={Pages.ReportsCost} element={<ReportsCostPage />} />
              <Route
                path={Pages.ReportsTrends}
                element={<ReportsTrendsPage />}
              />
              <Route path={Pages.Reports} element={<ReportsDefaultPage />} />
            </Route>
            <Route
              path={Pages.UsersManagement}
              element={<UsersManagementPage />}
            />
            <Route
              path={Pages.InsightsSettings}
              element={<InsightsSettingsPage />}
            />
            <Route path={Pages.Logout} element={<LogoutPage />} />
            <Route path={"*"} element={<DashboardDefaultPage />} />
          </Route>
        </Route>
      </Routes>
    </div>
  );
};

export default AppRoutes;
